// A customer's VPS on the account page: what the card says about the server.
//
// On 2026-10-30 a Nook's IP was findable only with doctl, and the dashboard
// card said "Active" for a box whose CyberPanel install had failed. This turns
// squish-backend's server view (lib/vps-server-view.js there) into the lines a
// rep reads. Lives outside the component so it runs under `node --test`.
//
// Two inputs:
//   summary  the `server` block on the hosting product row (registry only,
//            there at first paint)
//   live     GET /api/hosting-subs/:domain/server (adds the droplet read and
//            the DNS/HTTPS checks); null until it answers
//
// The panel never reads a credential. The admin password keeps its own
// audited reveal.

const PANEL_INSTALL = {
  installed: { text: 'installed', tone: 'ok' },
  ok: { text: 'installed', tone: 'ok' },
  failed: { text: 'install FAILED', tone: 'bad' },
  installing: { text: 'installing', tone: 'warn' },
  // The live view connects to the panel's port (squish-backend
  // lib/vps-server-view). This is the only answer for a box built before the
  // install report: markhamill.com called back "ready" with nothing on :8090.
  answering: { text: 'answering on its port', tone: 'ok' },
  not_answering: { text: 'NOT INSTALLED', tone: 'bad' },
  callback_received: { text: 'box called back; panel not verified', tone: 'warn' },
  unknown: { text: 'install not recorded', tone: 'warn' },
};

export function panelInstallLine(panel, install) {
  const name = panel || 'No panel';
  if (!panel || panel === 'None') return { text: name, tone: 'muted' };
  const s = PANEL_INSTALL[install?.state] || { text: String(install?.state || 'install not recorded'), tone: 'warn' };
  return { text: `${name} · ${s.text}${install?.error ? ` — ${install.error}` : ''}`, tone: s.tone };
}

// The facts, in reading order. Each row: { label, value, tone, copy? }.
export function vpsFacts(summary, live = null) {
  const s = { ...(summary || {}), ...(live && !live.error ? live : {}) };
  const rows = [];

  const name = live?.droplet?.name || null;
  rows.push({
    label: 'Droplet',
    value: s.dropletId ? `${name ? `${name} · ` : ''}#${s.dropletId}` : 'none recorded',
    tone: s.dropletId ? 'normal' : 'bad',
    copy: s.dropletId || null,
  });

  let ipValue;
  let ipTone = 'normal';
  if (s.publicIpv4) {
    ipValue = s.publicIpv4;
    if (live?.ipMismatch) { ipValue += ' (registry had a different IP)'; ipTone = 'warn'; }
  } else if (!live) {
    ipValue = 'not in the registry — checking the droplet…';
    ipTone = 'muted';
  } else {
    ipValue = live?.droplet?.error ? `unknown — ${live.droplet.error}` : 'none assigned';
    ipTone = 'bad';
  }
  rows.push({ label: 'IPv4', value: ipValue, tone: ipTone, copy: s.publicIpv4 || null });

  rows.push({ label: 'Region', value: s.region || '—', tone: 'normal' });
  rows.push({ label: 'Size', value: s.size || '—', tone: 'normal' });

  const p = panelInstallLine(s.panel, s.panelInstall);
  rows.push({ label: 'Panel', value: p.text, tone: p.tone });

  const dropletStatus = live?.droplet?.status;
  rows.push({
    label: 'Status',
    value: `${s.vpsStatus || 'unknown'}${dropletStatus ? ` · droplet ${dropletStatus}` : ''}`,
    tone: s.vpsStatus === 'active' && (!dropletStatus || dropletStatus === 'active') ? 'ok' : 'warn',
  });

  const domains = Array.isArray(s.attachedDomains) ? s.attachedDomains : [];
  rows.push({
    label: 'Domain',
    value: domains.length ? domains.join(', ') : 'none attached yet',
    tone: domains.length ? 'normal' : 'warn',
  });
  return rows;
}

// One line per attached domain: does it point here, does it answer HTTPS?
export function domainCheckLine(check) {
  if (!check) return null;
  if (check.dnsError) return { text: `${check.domain}: does not resolve (${check.dnsError})`, tone: 'bad' };
  const ips = (check.resolvesTo || []).join(', ') || 'nothing';
  let dns;
  let tone = 'ok';
  if (check.pointsHere) dns = 'points here';
  else if (check.viaCdn) dns = `via CDN (${ips})`;
  else if (check.pointsHere === false) { dns = `points elsewhere (${ips})`; tone = 'bad'; }
  else dns = `resolves to ${ips}`;
  let https;
  if (!check.https) https = 'HTTPS not checked';
  else if (check.https.ok) https = `HTTPS ${check.https.status}`;
  else { https = `HTTPS fails${check.https.status ? ` (${check.https.status})` : check.https.error ? ` (${check.https.error})` : ''}`; tone = 'bad'; }
  return { text: `${check.domain}: ${dns} · ${https}`, tone };
}

// The attach action is offered exactly where the server would accept it:
// a placeholder site, active, on a VPS that finished provisioning. Anything
// else gets the reason instead of a button that 409s.
export function attachState(summary) {
  if (!summary) return { offer: false, reason: null };
  if (!summary.isPlaceholder) return { offer: false, reason: null };
  if ((summary.status || 'active') !== 'active') return { offer: false, reason: `site is ${summary.status}` };
  if (summary.vpsStatus !== 'active') return { offer: false, reason: 'server is still provisioning' };
  return { offer: true, reason: null };
}

const DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;
export function normaliseDomainInput(raw) {
  const d = String(raw || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
  if (!DOMAIN_RE.test(d) || /\.squish\.internal$/.test(d)) return null;
  return d;
}

export const TONE_COLOR = {
  ok: '#4ade80',
  warn: '#fbbf24',
  bad: '#f87171',
  muted: 'var(--text3)',
  normal: 'var(--text)',
};
